import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import PasswordSettings from '../components/admin/PasswordSettings';

const { FiDatabase, FiDownload, FiTrash2, FiLock, FiLogOut, FiFileText, FiUser, FiSettings } = FiIcons;

export default function Admin() {
  const navigate = useNavigate();
  const [submissions, setSubmissions] = useState([]);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [passwordInput, setPasswordInput] = useState('');
  const [error, setError] = useState('');
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);

  // Helper para obtener la contraseña actual (personalizada o por defecto)
  const getStoredPassword = () => {
    return localStorage.getItem('cb_admin_password') || import.meta.env.VITE_ADMIN_PASSWORD || 'ComplianceBox2024';
  };

  useEffect(() => {
    const sessionAuth = sessionStorage.getItem('admin_auth');
    if (sessionAuth === 'true') {
      setIsAuthenticated(true);
      loadSubmissions();
    }
  }, []);

  const loadSubmissions = () => {
    const data = JSON.parse(localStorage.getItem('cb_submissions') || '[]');
    setSubmissions(data);
  };

  const handleLogin = (e) => {
    e.preventDefault();
    const correctPassword = getStoredPassword();
    
    if (passwordInput === correctPassword) {
      setIsAuthenticated(true);
      sessionStorage.setItem('admin_auth', 'true');
      loadSubmissions();
      setError('');
    } else {
      setError('Contraseña incorrecta.');
      setPasswordInput('');
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    sessionStorage.removeItem('admin_auth');
  };

  const exportCSV = () => {
    if (submissions.length === 0) return;
    const headers = ['Fecha', 'Empresa', 'Contacto', 'Email', 'País', 'Score'];
    const rows = submissions.map(s => [
      new Date(s.date).toLocaleDateString(),
      s.businessInfo.companyName,
      s.businessInfo.contactName,
      s.businessInfo.email,
      s.businessInfo.country,
      `${s.results.percentage}%`
    ]);
    const csvContent = "data:text/csv;charset=utf-8," + [headers.join(','), ...rows.map(e => e.join(','))].join('\n');
    const link = document.createElement("a");
    link.setAttribute("href", encodeURI(csvContent));
    link.setAttribute("download", "diagnosticos.csv");
    link.click();
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-[80vh] flex items-center justify-center px-4 bg-slate-950">
        <div className="max-w-md w-full bg-slate-900 rounded-2xl shadow-xl border border-slate-800 p-8">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-blue-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <SafeIcon icon={FiLock} className="text-blue-500 text-3xl" />
            </div>
            <h1 className="text-2xl font-bold text-white">Acceso Admin</h1>
          </div>
          <form onSubmit={handleLogin} className="space-y-4">
            <input 
              autoFocus 
              type="password" 
              value={passwordInput} 
              onChange={(e) => setPasswordInput(e.target.value)} 
              className="w-full p-3 bg-slate-950 border border-slate-800 rounded-lg text-white outline-none focus:ring-2 focus:ring-blue-500" 
              placeholder="Contraseña" 
            />
            {error && <p className="text-red-500 text-sm">{error}</p>}
            <button type="submit" className="w-full py-3 bg-blue-600 hover:bg-blue-500 text-white rounded-lg font-bold transition-colors">
              Ingresar
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 py-12 px-4 sm:px-6">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
          <div>
            <h1 className="text-3xl font-bold text-white flex items-center gap-3">
              <SafeIcon icon={FiDatabase} className="text-blue-500" /> Panel de Administración
            </h1>
            <p className="text-slate-400 text-sm mt-1">Gestiona los diagnósticos realizados por tus clientes.</p>
          </div>
          <div className="flex flex-wrap gap-3">
            <button 
              onClick={() => setIsSettingsOpen(true)}
              className="px-4 py-2 bg-slate-900 border border-slate-800 rounded-lg text-slate-300 hover:bg-slate-800 flex items-center gap-2 transition-colors"
            >
              <SafeIcon icon={FiSettings} /> Ajustes
            </button>
            <button onClick={exportCSV} className="px-4 py-2 bg-slate-900 border border-slate-800 rounded-lg text-slate-300 hover:bg-slate-800 flex items-center gap-2 transition-colors">
              <SafeIcon icon={FiDownload} /> Exportar CSV
            </button>
            <button onClick={handleLogout} className="px-4 py-2 bg-red-600/10 text-red-500 border border-red-500/20 rounded-lg hover:bg-red-600 hover:text-white flex items-center gap-2 transition-all">
              <SafeIcon icon={FiLogOut} /> Cerrar Sesión
            </button>
          </div>
        </div>

        <div className="bg-slate-900 rounded-2xl shadow-md border border-slate-800 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm whitespace-nowrap">
              <thead className="bg-slate-950 border-b border-slate-800 text-slate-400 font-bold uppercase tracking-wider">
                <tr>
                  <th className="p-5">Fecha</th>
                  <th className="p-5">Empresa / Contacto</th>
                  <th className="p-5">Puntaje</th>
                  <th className="p-5">Nivel de Riesgo</th>
                  <th className="p-5 text-center">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800">
                {submissions.length === 0 ? (
                  <tr><td colSpan="5" className="p-12 text-center text-slate-500 italic">No hay registros almacenados en este navegador.</td></tr>
                ) : (
                  submissions.map((sub) => (
                    <tr key={sub.id} className="hover:bg-slate-800/50 transition-colors">
                      <td className="p-5 text-slate-400">{new Date(sub.date).toLocaleDateString()}</td>
                      <td className="p-5">
                        <div className="font-bold text-white">{sub.businessInfo.companyName}</div>
                        <div className="text-xs text-blue-400 flex items-center gap-1 mt-0.5">
                          <SafeIcon icon={FiUser} className="text-[10px]" />
                          {sub.businessInfo.contactName} • {sub.businessInfo.email}
                        </div>
                      </td>
                      <td className="p-5">
                        <span className={`px-3 py-1 rounded-full text-xs font-black ${sub.results.riskColor.replace('text-', 'bg-').replace('500', '500/10')} ${sub.results.riskColor} border border-current/20`}>
                          {sub.results.percentage}%
                        </span>
                      </td>
                      <td className="p-5 text-slate-300">{sub.results.riskLevel.split('—')[0]}</td>
                      <td className="p-5 text-center">
                        <button 
                          onClick={() => navigate(`/results/${sub.id}`)} 
                          className="px-4 py-2 bg-blue-600/10 text-blue-400 rounded-lg hover:bg-blue-600 hover:text-white flex items-center gap-2 mx-auto transition-all font-bold border border-blue-500/20"
                        >
                          <SafeIcon icon={FiFileText} /> Ver Reporte
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <PasswordSettings 
        isOpen={isSettingsOpen} 
        onClose={() => setIsSettingsOpen(false)} 
        currentStoredPassword={getStoredPassword()}
      />
    </div>
  );
}